import axios, { AxiosError, AxiosHeaders, type InternalAxiosRequestConfig } from "axios";

import { env } from "@/lib/env";
import { getAccessToken, supabase } from "@/lib/supabase";

/**
 * Kullanıcıya gösterilebilir hata.
 *
 * PostgREST ve Edge Function'lar hatayı aynı biçimde döndürüyor
 * (`{ message, code }`), veritabanı fonksiyonlarındaki `raise exception`
 * mesajları da zaten Türkçe ve kullanıcıya yönelik. Bu sınıf o mesajı tek bir
 * yerde toplayıp arayüzün her yerinde `error.message` demesini sağlıyor.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Oturum sorunu mu? Yönlendirme kararı buna bakıyor. */
  get isAuthError(): boolean {
    return this.status === 401;
  }
}

interface PostgrestErrorBody {
  message?: string;
  details?: string;
  hint?: string;
  code?: string;
  error_description?: string;
  error?: string;
}

/** İstek yeniden denendi mi — sonsuz yenileme döngüsünü keser. */
type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

export const http = axios.create({
  baseURL: env.supabaseUrl,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// -----------------------------------------------------------------------------
//  İstek: her çağrıya apikey ve taze erişim jetonu
// -----------------------------------------------------------------------------

http.interceptors.request.use(async (config) => {
  const headers = AxiosHeaders.from(config.headers);

  headers.set("apikey", env.supabaseAnonKey);

  // getSession() süresi dolmak üzere olan jetonu kendiliğinden yeniliyor;
  // jetonu modül düzeyinde saklamak yerine her istekte sormamızın nedeni bu.
  const token = await getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  config.headers = headers;
  return config;
});

// -----------------------------------------------------------------------------
//  Yanıt: tek biçimli hata + 401'de bir kez yenileme
// -----------------------------------------------------------------------------

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<PostgrestErrorBody>) => {
    const config = error.config as RetryableConfig | undefined;

    // Jeton isteğin tam ortasında geçersizleşmiş olabilir (ör. bilgisayar uykudan
    // döndü). Bir kez yenileyip aynı isteği tekrarlıyoruz; yine 401 ise oturum
    // gerçekten bitmiş demektir, çıkış yapılıyor ve rota koruması giriş ekranına
    // yönlendiriyor.
    if (error.response?.status === 401 && config && !config._retried) {
      config._retried = true;

      const { data, error: refreshError } = await supabase.auth.refreshSession();

      if (!refreshError && data.session) {
        return http.request(config);
      }

      await supabase.auth.signOut();
    }

    throw toApiError(error);
  },
);

function toApiError(error: AxiosError<PostgrestErrorBody>): ApiError {
  if (error.code === "ECONNABORTED") {
    return new ApiError(0, "İstek zaman aşımına uğradı. Bağlantınızı kontrol edip tekrar deneyin.", "timeout");
  }

  if (!error.response) {
    return new ApiError(0, "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.", "network_error");
  }

  const { status, data } = error.response;
  const message = data?.message ?? data?.error_description ?? data?.error;

  if (message) {
    return new ApiError(status, message, data?.code, data?.details ?? data?.hint);
  }

  if (status === 401) {
    return new ApiError(status, "Oturumunuz sonlandı. Yeniden giriş yapın.", "unauthenticated");
  }

  if (status === 403) {
    return new ApiError(status, "Bu işlem için yetkiniz yok.", "forbidden");
  }

  return new ApiError(status, `Beklenmeyen bir hata oluştu (HTTP ${status}).`, "unknown");
}

/** Herhangi bir hatayı gösterilebilir metne çevirir. */
export function errorMessage(error: unknown, fallback = "Beklenmeyen bir hata oluştu."): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

// =============================================================================
//  PostgREST
//
//  Okuma yolu. Filtreleme/sıralama PostgREST söz dizimiyle (`title=ilike.*abc*`)
//  sorgu dizesine geçiyor; sunucuda ayrıca uç nokta yazmaya gerek kalmıyor.
// =============================================================================

export type QueryParams = Record<string, string | number | boolean | undefined>;

function cleanParams(params?: QueryParams): Record<string, string> {
  if (!params) return {};

  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => [key, String(value)]),
  );
}

/** `GET /rest/v1/<kaynak>` — dizi döner. */
export async function restList<T>(resource: string, params?: QueryParams): Promise<T[]> {
  const { data } = await http.get<T[]>(`/rest/v1/${resource}`, { params: cleanParams(params) });
  return data ?? [];
}

/**
 * Tek satır bekleyen okuma. `Accept: ...object+json` başlığı PostgREST'e tek
 * nesne döndürmesini söylüyor; satır yoksa 406 yerine null dönsün diye
 * `maybeSingle` davranışı burada elle kuruluyor.
 */
export async function restMaybeSingle<T>(resource: string, params?: QueryParams): Promise<T | null> {
  const rows = await restList<T>(resource, { ...params, limit: 1 });
  return rows[0] ?? null;
}

/** `POST /rest/v1/rpc/<ad>` — veritabanı fonksiyonu çağrısı. */
export async function rpc<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data } = await http.post<T>(`/rest/v1/rpc/${name}`, args);
  return data;
}

// =============================================================================
//  Edge Functions
//
//  Yazma yolunun kripto gerektiren kısmı: lisans üretimi ve doğrulama, hesap
//  açma. Özel anahtar yalnızca orada.
// =============================================================================

export async function invokeFunction<T>(name: string, body: Record<string, unknown> = {}): Promise<T> {
  const { data } = await http.post<T>(`/functions/v1/${name}`, body);
  return data;
}
